<template>

    <ControlWrapper v-if="initailised"
                    v-loading="loading"
                    :wrapper-title="$t('mergeSegment.mergeSgments')"
                    @confirm="saveMergeAction"
                    @cancel="closeMergeAction">

        <div v-if="isIdml"
             class="idml-style-note">
            <span class="material-symbols-outlined">info</span>
            {{ $t('mergeSegment.idmlStyleNote') }}
        </div>

        <div id="merge-source"
             calss="merge-source">
            <div class="title">
                {{$t('mergeSegment.sourceSegments')}}
            </div>

            <div v-for="(segment, index) in segmentsToMerge"
                 :key="'m'+segment.itemId">
                <SimpleSegment v-model="segment.data.sourceData"
                               :sample-segment="true">

                    <div id="remove-merge-segment"
                         v-if="showRemove(index)">
                        <el-tooltip placement="top"
                                    :content="removeTooltip"
                                    :show-after="10">

                            <el-button type="default"
                                       @click="removeLineAction">
                                {{$t('mergeSegment.remove')}}
                            </el-button>
                        </el-tooltip>
                    </div>
                </SimpleSegment>

                <div v-if="showParagraphBoundary(index)"
                     class="paragraph-boundary">
                    <div class="boundary-line"></div>
                    <span class="boundary-text">{{ endOfBlockInfoMessage }}</span>
                    <div class="boundary-line"></div>
                </div>
            </div>

            <el-tooltip placement="top"
                        :content="addTooltip"
                        :show-after="10">

                <div id="add-merge-segment"
                     v-if="showAddButton">

                    <el-button type="warning"
                               :loading="isLoadingContinuation"
                               @click="addLineAction">
                        {{$t('mergeSegment.add')}}
                    </el-button>

                </div>

            </el-tooltip>

            <div v-if="showParagraphEndInfo"
                 class="end-of-paragraph-message info">
                <span class="material-symbols-outlined">info</span>
                {{ endOfBlockInfoMessage }}
            </div>

            <div v-if="showNoMoreSegments"
                 class="end-of-paragraph-message warning">
                <span class="material-symbols-outlined">block</span>
                {{ $t('mergeSegment.noMoreSegments') }}
            </div>

            <div v-if="showParagraphEndBlock"
                 class="end-of-paragraph-message warning">
                <span class="material-symbols-outlined">info</span>
                {{ endOfBlockMessage }}
            </div>

        </div>

        <div id="merge-result"
             class="merge-result">
            <div class="title">
                {{$t('mergeSegment.mergeResult')}}
            </div>

            <SimpleSegment v-model="mergedSourceData">
                <div v-if="resultSegmentTooLong(mergedSourceData)">
                    <SegmentStatusIcon />
                </div>
            </SimpleSegment>

        </div>

    </ControlWrapper>

</template>

<script lang="ts">
    import { defineComponent } from 'vue';
    import { useI18n } from 'vue-i18n'
    import { mergeStore } from '@/stores/mergeStore'
    import { documentStore } from '@/stores/documentStore'

    import { notifyWarning, notifyInfo } from '@/services/notificationService';
    import { mergeSegmentService } from './mergeSegmentService'
    import { segmentTextLogicService } from '@/services/translationSegment/segmentTextLogicService';
    import { shortcuts } from '@/utils/shortcuts';

    import { SegmentEditorItem } from '@/models/SegmentEditorItem';
    import { DocumentType } from '@/models/DocumentType';
    import { getMergeKey } from '@/models/segmentMergeKey';

    import ControlWrapper from './ControlWrapper.vue'
    import SimpleSegment from './SimpleSegment.vue'
    import SegmentStatusIcon from '@/components/SegmentStatusIcon.vue';

    export default defineComponent({
        props: {
            modelValue: Object,
        },
        components: {
            SimpleSegment,
            ControlWrapper,
            SegmentStatusIcon,
        },
        data() {
            return {
                addTooltip: shortcuts.getTooltipTranslated(this.$t, shortcuts.addSegmentToMerge),
                removeTooltip: shortcuts.getTooltipTranslated(this.$t, shortcuts.removeSegmentToMerge),

                initailised: false,
                loading: false,
                mergeSegmentService: new mergeSegmentService(),
                segmentTextLogicService: segmentTextLogicService.getInstance(),
                mergeStore: mergeStore(),
                documentStore: documentStore(),

                mergeResult: {},
            };
        },
        computed: {
            segmentItem: {
                get(): SegmentEditorItem { return this.modelValue as SegmentEditorItem; },
                set(value: SegmentEditorItem) { this.$emit('input', value); }
            },
            segmentsToMerge(): SegmentEditorItem[] {
                return this.mergeSegmentService.getSegmentsToMerge();
            },
            mergedSourceData(): any {
                return this.mergeSegmentService.getMergeSourceData();
            },
            isLoadingContinuation(): boolean {
                return this.mergeStore.continuationLoading;
            },
            isDocx(): boolean {
                return this.documentStore.documentType === DocumentType.Docx;
            },
            isXlsx(): boolean {
                return this.documentStore.documentType === DocumentType.Xlsx;
            },
            isPptx(): boolean {
                return this.documentStore.documentType === DocumentType.Pptx;
            },
            isIdml(): boolean {
                return this.documentStore.documentType === DocumentType.Idml;
            },
            endOfBlockMessage(): string {
                if (this.isXlsx) {
                    return this.t('mergeSegment.endOfCell');
                } else if (this.isPptx) {
                    return this.t('mergeSegment.endOfTextBlock');
                } else if (this.isIdml) {
                    return this.t('mergeSegment.endOfStory');
                }
                return this.t('mergeSegment.endOfParagraph');
            },
            endOfBlockInfoMessage(): string {
                if (this.isXlsx) {
                    return this.t('mergeSegment.endOfCellInfo');
                } else if (this.isPptx) {
                    return this.t('mergeSegment.endOfTextBlockInfo');
                } else if (this.isIdml) {
                    return this.t('mergeSegment.endOfStoryInfo');
                }
                return this.t('mergeSegment.endOfParagraphInfo');
            },
            endOfBlockTitle(): string {
                if (this.isXlsx) {
                    return this.t('mergeSegment.endOfCellTitle');
                } else if (this.isPptx) {
                    return this.t('mergeSegment.endOfTextBlockTitle');
                } else if (this.isIdml) {
                    return this.t('mergeSegment.endOfStoryTitle');
                }
                return this.t('mergeSegment.endOfParagraphTitle');
            },
            showAddButton(): boolean {
                if (!this.mergeStore.hasMoreSegments) {
                    return false;
                }
                if (this.isDocx) {
                    return true;
                }
                return !this.mergeStore.isAtParagraphEnd;
            },
            showParagraphEndInfo(): boolean {
                return this.isDocx &&
                    this.mergeStore.isAtParagraphEnd &&
                    this.mergeStore.hasMoreSegments;
            },
            showNoMoreSegments(): boolean {
                return !this.mergeStore.hasMoreSegments;
            },
            showParagraphEndBlock(): boolean {
                return !this.isDocx &&
                    this.mergeStore.isAtParagraphEnd &&
                    this.mergeStore.hasMoreSegments;
            },
        },
        async created() {
            const success = await this.mergeSegmentService.initMerge(this.segmentItem.itemId);

            if (!success) {
                notifyWarning(
                    this.endOfBlockTitle,
                    this.t('mergeSegment.noSegmentsToMerge')
                );
                this.mergeSegmentService.cancelMerge();
            } else {
                this.initailised = true;
            }
        },
        mounted() {
            document.addEventListener("keydown", this.hotkeysListener);
        },
        beforeUnmount() {
            document.removeEventListener("keydown", this.hotkeysListener);
        },
        setup() {
            const { t } = useI18n()
            return { t }
        },
        methods: {
            async hotkeysListener(e: KeyboardEvent) {
                if (e.key === "ArrowDown" && e.ctrlKey) {
                    if (this.showAddButton) {
                        await this.addLineAction()
                    } else {
                        notifyWarning(
                            this.endOfBlockTitle,
                            this.showNoMoreSegments
                                ? this.t('mergeSegment.noMoreSegments')
                                : this.endOfBlockMessage
                        );
                    }
                    return
                }

                if (e.key === "ArrowUp" && e.ctrlKey) {
                    this.removeLineAction()
                    notifyInfo(this.t('mergeSegment.removed'), this.t('mergeSegment.removedLine'));
                    return
                }
            },
            showRemove(index: number) {
                return this.segmentsToMerge.length == (index + 1);
            },

            showParagraphBoundary(index: number): boolean {
                const segments = this.segmentsToMerge;

                // Don't show after the last segment
                if (index >= segments.length - 1) {
                    return false;
                }
                const currentSegment = segments[index];
                const nextSegment = segments[index + 1];

                const currentKey = getMergeKey(currentSegment.data);
                const nextKey = getMergeKey(nextSegment.data);

                return currentKey !== nextKey;
            },

            removeLineAction() {
                this.mergeSegmentService.decreaseSegments();
            },

            async addLineAction() {
                const result = await this.mergeSegmentService.increaseSegments();

                if (result.success) {
                    notifyInfo(this.t('mergeSegment.added'), this.t('mergeSegment.addedNewLine'));
                } else if (result.endOfParagraph) {
                    notifyWarning(
                        this.endOfBlockTitle,
                        this.showNoMoreSegments
                            ? this.t('mergeSegment.noMoreSegments')
                            : this.endOfBlockMessage
                    );
                }
            },

            async saveMergeAction() {
                this.loading = true;
                await this.mergeSegmentService.mergeSegments();
            },

            closeMergeAction() {
                this.mergeSegmentService.cancelMerge();
            },

            resultSegmentTooLong(mergedSourceData: any) {
                return this.segmentTextLogicService.textIsTooLong(mergedSourceData.text);
            },

        },
    });

</script>

<style lang="scss" scoped>
    .title {
        display: flex;
        margin: 10px 5px;
        color: var(--white)
    }

    .end-of-paragraph-message {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 5px;
        font-size: 16px;

        .material-symbols-outlined {
            font-size: 18px;
        }

        &.info {
            color: var(--info-color, #909399);
        }

        &.warning {
            color: var(--warning-color, #e6a23c);
        }
    }

    .idml-style-note {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 0 5px 12px;
        font-size: 16px;
        color: var(--info-color, #909399);
        text-align: left;

        .material-symbols-outlined {
            font-size: 16px;
            flex-shrink: 0;
            margin-top: 1px;
        }
    }

    .paragraph-boundary {
        display: flex;
        align-items: center;
        margin: 8px 0;
        padding: 4px 0;

        .boundary-line {
            flex: 1;
            height: 1px;
            background-color: var(--info-color, #909399);
            opacity: 0.5;
        }

        .boundary-text {
            color: var(--info-color, #909399);
            padding: 0 12px;
            font-size: 12px;
            white-space: nowrap;
        }
    }
</style>